import React from "react";
import { useLang } from "@/context/LanguageContext";
import { LogIn } from "lucide-react";

/**
 * Site-wide footer.
 *
 * Layout: 6 logical columns on desktop, 3 on tablet, 1 on mobile.
 * The "Advertise" column used to be a 17-item dumping ground mixing
 * publisher-login / advertiser CTAs / calculators / contact — confusing
 * for both users ("where do I sign in?") and crawlers (poor topical
 * grouping). It's now split into two dedicated columns ("For app
 * owners" and "Tools & resources") with subtle sub-dividers, and the
 * App-owner sign-in is pulled out as a primary CTA at the top of its
 * column with a key-icon so returning publishers can spot it at a
 * glance.
 */
const Footer = () => {
  const { t, lang } = useLang();
  const isNl = lang === "nl";
  const year = new Date().getFullYear();

  const linkCls = "hover:text-[#2563EB] transition-colors";

  // Tool links grouped for clarity:
  //   1. discovery (advisor / compare / trending)
  //   2. calculators & checkers
  //   3. about (contact)
  const discovery = [
    { href: "/advisor", label: t.nav.advisor },
    { href: "/compare", label: t.nav.compare },
    {
      href: isNl ? "/trending-analyses" : "/trending",
      label: isNl ? "Trending analyses" : "Trending analyses",
      icon: "🔥",
      cls: "hover:text-orange-600",
      testid: "footer-trending",
    },
  ];

  const tools = [
    {
      href: isNl ? "/paardengewicht-calculator" : "/horse-weight-calculator",
      label: isNl ? "Gewichtscalculator" : "Weight calculator",
      testid: "footer-weight-calc",
    },
    {
      href: isNl ? "/hooi-rantsoen-calculator" : "/hay-ration-calculator",
      label: isNl ? "Hooi-rantsoen calculator" : "Hay ration calculator",
      testid: "footer-hay-calc",
    },
    {
      href: isNl ? "/koliek-checker" : "/colic-symptom-checker",
      label: isNl ? "Koliek-checker" : "Colic checker",
      icon: "🆘",
      cls: "hover:text-red-600",
      testid: "footer-colic",
    },
    {
      href: isNl ? "/veulendatum-calculator" : "/foaling-date-calculator",
      label: isNl ? "Veulendatum calculator" : "Foaling date calculator",
      cls: "hover:text-pink-600",
      testid: "footer-foaling",
    },
    {
      href: isNl ? "/paardenleeftijd-calculator" : "/horse-age-calculator",
      label: isNl ? "Paardenleeftijd calculator" : "Horse age calculator",
      cls: "hover:text-indigo-600",
      testid: "footer-horseage",
    },
    {
      href: isNl ? "/paardenkosten-calculator" : "/horse-cost-calculator",
      label: isNl ? "Paardenkosten calculator" : "Horse cost calculator",
      cls: "hover:text-emerald-600",
      testid: "footer-cost",
    },
    {
      href: isNl ? "/hoefsmid-planner" : "/farrier-schedule-calculator",
      label: isNl ? "Hoefsmid-planner" : "Farrier schedule",
      cls: "hover:text-stone-700",
      testid: "footer-farrier",
    },
    {
      href: isNl ? "/trailer-gewicht-calculator" : "/trailer-weight-calculator",
      label: isNl ? "Trailer-gewicht calculator" : "Trailer weight calculator",
      cls: "hover:text-blue-600",
      testid: "footer-trailer",
    },
  ];

  // App-owner links: keep the most-clicked ones (List your app, Premium,
  // Auction, Media kit, Sponsor inquiry) together. The "Sign-in" link is
  // hoisted into its own framed CTA above so returning publishers don't
  // have to scan a long mixed list.
  const ownerLinks = [
    { href: "/submit-app", label: isNl ? "Meld je app aan" : "List your app", testid: "footer-submit-app" },
    {
      href: "/premium-listing",
      label: isNl ? "Premium listing" : "Premium listing",
      icon: "★",
      cls: "hover:text-amber-600",
      testid: "footer-premium-listing",
      iconCls: "text-amber-500",
    },
    {
      href: "/auction",
      label: isNl ? "Weekly auction" : "Weekly auction",
      icon: "⚡",
      cls: "hover:text-amber-600",
      testid: "footer-auction",
      iconCls: "text-amber-500",
    },
    { href: "/sponsor", label: t.sponsor.cta },
    {
      href: `${process.env.REACT_APP_BACKEND_URL}/api/media-kit.pdf`,
      label: isNl ? "Media kit (PDF)" : "Media kit (PDF)",
      icon: "📄",
      external: true,
      testid: "footer-mediakit",
    },
  ];

  const renderLink = (item) => {
    const cls = `inline-flex items-center gap-1 ${item.cls || linkCls}`;
    const inner = (
      <>
        {item.icon && <span className={`text-[11px] ${item.iconCls || ""}`}>{item.icon}</span>}
        <span>{item.label}</span>
      </>
    );
    if (item.external) {
      return (
        <a href={item.href} target="_blank" rel="noreferrer" className={cls} data-testid={item.testid}>
          {inner}
        </a>
      );
    }
    return (
      <a href={item.href} className={cls} data-testid={item.testid}>
        {inner}
      </a>
    );
  };

  return (
    <footer className="border-t border-[#E2E8F0] bg-[#F1F5F9] mt-24">
      <div className="hac-container py-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-sm text-[#64748B]">
        {/* Col 1 — Brand */}
        <div className="lg:col-span-1">
          <div className="font-heading text-2xl mb-3">
            <span className="text-[#0F172A]">Horse</span>
            <span className="text-[#2563EB]">App</span>
            <span className="text-[#0F172A]">Compare</span>
          </div>
          <p className="leading-relaxed">{t.footer.tagline}</p>
        </div>

        {/* Col 2 — Categories */}
        <div>
          <div className="overline mb-3">{t.nav.categories}</div>
          <ul className="space-y-2">
            {Object.entries(t.categories).map(([k, v]) => (
              <li key={k}>
                <a href={`/categories/${k}`} className={linkCls}>{v}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 — Regions (top) + SEO Guides (below), stacked */}
        <div className="space-y-8">
          <div>
            <div className="overline mb-3">{t.common.regions}</div>
            <ul className="space-y-2">
              {t.regions.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-3">SEO Guides</div>
            <ul className="space-y-2">
              <li><a href="/best-of/training" className={linkCls}>Best Training Apps 2026</a></li>
              <li><a href="/best-of/stable" className={linkCls}>Best Stable Apps 2026</a></li>
              <li><a href="/best-of/ai" className={linkCls}>Best AI Horse Apps</a></li>
              <li><a href="/best-horse-apps" className={linkCls}>Best Horse Apps 2026</a></li>
              <li><a href="/beste-paardenapps" className={linkCls}>Beste Paardenapps (NL)</a></li>
              <li><a href="/best/riding-tracker" className={linkCls}>Best Riding Tracker Apps</a></li>
              <li><a href="/best/breeding-software" className={linkCls}>Best Breeding Software</a></li>
              <li><a href="/versus/equisight-vs-equilab" className={linkCls}>EquiSight vs Equilab</a></li>
            </ul>
          </div>
        </div>

        {/* Col 4 — For App Owners */}
        <div>
          <div className="overline mb-3">{isNl ? "Voor app-eigenaren" : "For app owners"}</div>

          {/* Framed sign-in CTA — primary action returning publishers look for */}
          <a
            href="/publisher/login"
            data-testid="footer-publisher-login"
            className="group flex items-center gap-2 mb-4 bg-white border border-[#E2E8F0] hover:border-[#2563EB] hover:bg-[#EFF6FF] rounded-lg px-3 py-2 transition-colors"
          >
            <LogIn size={14} className="text-[#2563EB] shrink-0" />
            <span className="text-[#0F172A] font-medium text-xs group-hover:text-[#2563EB]">
              {isNl ? "App-eigenaar inloggen" : "App owner sign-in"}
            </span>
          </a>

          <ul className="space-y-2">
            {ownerLinks.map((item) => (
              <li key={item.href}>{renderLink(item)}</li>
            ))}
          </ul>
        </div>

        {/* Col 5+6 — Tools & Resources (col-span-2 with inner 2-col grid for the calculators) */}
        <div className="lg:col-span-2 sm:col-span-2 md:col-span-1">
          <div className="overline mb-3">{isNl ? "Tools & resources" : "Tools & resources"}</div>

          {/* Discovery row spans full width */}
          <ul className="space-y-2 mb-3">
            {discovery.map((item) => (
              <li key={item.href}>{renderLink(item)}</li>
            ))}
          </ul>

          <div className="border-t border-[#E2E8F0] my-3" />

          {/* Calculators in a 2-column inner grid so the column heights stay balanced */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {tools.map((item) => (
              <li key={item.href}>{renderLink(item)}</li>
            ))}
          </ul>

          <div className="border-t border-[#E2E8F0] my-3" />

          <ul className="space-y-2">
            <li>
              <a href="/contact" className={linkCls} data-testid="footer-contact">
                {isNl ? "Contact" : "Contact"}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="hac-container pb-8 text-xs text-[#94A3B8] flex flex-col md:flex-row justify-between gap-2">
        <span>© {year} HorseAppCompare. {t.footer.rights}</span>
        <span className="font-mono">v1.0 · Global Equine Software Intelligence</span>
      </div>
    </footer>
  );
};

export default Footer;
